// Fetch all agents (for admin dropdown)
export const getAgentsList = async () => {
  const response = await apiCall('/agents', { method: 'GET' });
  return response.data || [];
};
/**
 * API Service - Centralized API client for all backend communication
 * Base URL configured from environment variables
 */

// Default to local backend for development.
// In production, set REACT_APP_API_URL at build time:
//   REACT_APP_API_URL=https://api.kaammytra.com/api npm run build
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.kaammytra.com/api';

/**
 * Helper function to make API calls
 */
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Try to read response as text first so we can handle non-JSON responses
    const raw = await response.text();
    let data;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (parseErr) {
      // Not JSON, keep raw text
      data = raw;
    }

    if (!response.ok) {
      // Prefer structured message, fall back to raw text or status
      const msg = (data && typeof data === 'object' && (data.message || data.error)) || (typeof data === 'string' && data) || `API request failed with status ${response.status} ${response.statusText}`;
      const error = new Error(msg);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = data;
      error.raw = raw;
      throw error;
    }

    // Return parsed object when possible, otherwise return raw text
    return data !== null ? data : raw;
  } catch (err) {
    // Handle network errors or JSON parse errors
    if (err.status) {
      throw err; // Already formatted error
    }
    const error = new Error(err.message || 'Network error - please check your connection');
    error.status = 0;
    error.data = { message: err.message };
    throw error;
  }
};

/**
 * Authentication API calls
 */
export const authAPI = {
  register: (userData) =>
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  login: (credentials) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  refreshToken: (refreshToken) =>
    apiCall('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  logout: () =>
    apiCall('/auth/logout', {
      method: 'POST',
    }),

  getProfile: () =>
    apiCall('/auth/profile', {
      method: 'GET',
    }),

  changePassword: (passwordData) =>
    apiCall('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    }),
};

/**
 * Loan Types API calls
 */
export const loanTypesAPI = {
  create: (loanTypeData) =>
    apiCall('/loan-types', {
      method: 'POST',
      body: JSON.stringify(loanTypeData),
    }),

  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/loan-types?${queryString}`, {
      method: 'GET',
    });
  },

  get: (id) =>
    apiCall(`/loan-types/${id}`, {
      method: 'GET',
    }),

  update: (id, loanTypeData) =>
    apiCall(`/loan-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(loanTypeData),
    }),

  delete: (id) =>
    apiCall(`/loan-types/${id}`, {
      method: 'DELETE',
    }),

  getOptions: () =>
    apiCall('/loan-types', {
      method: 'GET',
    }),
};

/**
 * Retailers API calls
 */
export const retailersAPI = {
  // Create/register retailer
  register: (retailerData) =>
    apiCall('/retailers', {
      method: 'POST',
      body: JSON.stringify(retailerData),
    }),

  // Alias for register
  registerRetailer: (retailerData) =>
    apiCall('/retailers', {
      method: 'POST',
      body: JSON.stringify(retailerData),
    }),

  // List retailers (Admin gets all, Agent gets own)
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/retailers?${queryString}`, {
      method: 'GET',
    });
  },

  // Alias for pages using getRetailers
  getRetailers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiCall(`/retailers?${queryString}`, {
      method: 'GET',
    });
    return response.data || [];
  },

  // Alias for getAllRetailers  
  getAllRetailers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiCall(`/retailers?${queryString}`, {
      method: 'GET',
    });
    return response.data || [];
  },

  get: (id) =>
    apiCall(`/retailers/${id}`, {
      method: 'GET',
    }),

  update: (id, retailerData) =>
    apiCall(`/retailers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(retailerData),
    }),

  // Alias for updateRetailer
  updateRetailer: (id, retailerData) =>
    apiCall(`/retailers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(retailerData),
    }),

  // Update KYC status (Admin)
  updateKycStatus: (id, kycStatus) =>
    apiCall(`/retailers/${id}/kyc`, {
      method: 'PUT',
      body: JSON.stringify({ kycStatus }),
    }),

  // Generate KYC token for public QR upload
  generateKycToken: (retailerId) =>
    apiCall(`/kyc/retailers/${retailerId}/token`, {
      method: 'POST',
    }),

  delete: (id) =>
    apiCall(`/retailers/${id}`, {
      method: 'DELETE',
    }),

  // Alias for deleteRetailer
  deleteRetailer: (id) =>
    apiCall(`/retailers/${id}`, {
      method: 'DELETE',
    }),

  uploadDocument: (retailerId, formData) =>
    fetch(`${API_BASE_URL}/retailers/${retailerId}/documents`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: formData,
    }).then((res) => {
      if (!res.ok) {
        throw new Error('Failed to upload document');
      }
      return res.json();
    }),

  getDocuments: (id) =>
    apiCall(`/retailers/${id}/documents`, {
      method: 'GET',
    }),
};

/**
 * Loans API calls
 */
export const loansAPI = {
  submit: (loanData) =>
    apiCall('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    }),

  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/loans?${queryString}`, {
      method: 'GET',
    });
  },

  get: (id) =>
    apiCall(`/loans/${id}`, {
      method: 'GET',
    }),

  approve: (id, approvalData) =>
    apiCall(`/loans/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(approvalData),
    }),

  reject: (id, rejectionData) =>
    apiCall(`/loans/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(rejectionData),
    }),

  close: (id, closeData) =>
    apiCall(`/loans/${id}/close`, {
      method: 'POST',
      body: JSON.stringify(closeData),
    }),

  getAllLoans: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/loans?${queryString}`, {
      method: 'GET',
    });
  },
};

/**
 * Collections API calls
 */
export const collectionsAPI = {
  record: (collectionData) =>
    apiCall('/collections', {
      method: 'POST',
      body: JSON.stringify(collectionData),
    }),

  getToday: () =>
    apiCall('/collections/today', {
      method: 'GET',
    }),

  getHistory: (loanId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections/${loanId}/history?${queryString}`, {
      method: 'GET',
    });
  },

  getStats: (loanId) =>
    apiCall(`/collections/${loanId}/stats`, {
      method: 'GET',
    }),

  getSummary: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections/summary/date?${queryString}`, {
      method: 'GET',
    });
  },

  getAllCollections: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections?${queryString}`, {
      method: 'GET',
    });
  },
};

/**
 * Investments API calls
 */
export const investmentsAPI = {
  addFunds: (investmentData) =>
    apiCall('/investments', {
      method: 'POST',
      body: JSON.stringify(investmentData),
    }),

  getFundBalance: () =>
    apiCall('/investments/balance/current', {
      method: 'GET',
    }),

  getSummary: () =>
    apiCall('/investments/summary/all', {
      method: 'GET',
    }),

  getUtilization: () =>
    apiCall('/investments/report/utilization', {
      method: 'GET',
    }),

  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/investments?${queryString}`, {
      method: 'GET',
    });
  },

  get: (id) =>
    apiCall(`/investments/${id}`, {
      method: 'GET',
    }),
};

/**
 * Reports API calls
 */
export const reportsAPI = {
  getAdminDashboard: () =>
    apiCall('/reports/dashboard/admin', {
      method: 'GET',
    }),

  getAgentDashboard: () =>
    apiCall('/reports/dashboard/agent', {
      method: 'GET',
    }),

  getLedger: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/reports/ledger?${queryString}`, {
      method: 'GET',
    });
  },

  getCollectionsReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/reports/collections?${queryString}`, {
      method: 'GET',
    });
  },

  getPerformanceReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/reports/performance?${queryString}`, {
      method: 'GET',
    });
  },

  getAgentCollectionSummary: () =>
    apiCall('/reports/agent-collection-summary', {
      method: 'GET',
    }),

  getRetailerDetails: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/reports/retailer-details?${queryString}`, {
      method: 'GET',
    });
  },
};

/**
 * Agents API calls
 */
export const agentsAPI = {
  create: (agentData) =>
    apiCall('/agents', {
      method: 'POST',
      body: JSON.stringify(agentData),
    }),

  // Alias for createAgent
  createAgent: (agentData) =>
    apiCall('/agents', {
      method: 'POST',
      body: JSON.stringify(agentData),
    }),

  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/agents?${queryString}`, {
      method: 'GET',
    });
  },

  // Alias for getAgents - returns the data array directly
  getAgents: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiCall(`/agents?${queryString}`, {
      method: 'GET',
    });
    return response.data || [];
  },

  get: (id) =>
    apiCall(`/agents/${id}`, {
      method: 'GET',
    }),

  update: (id, agentData) =>
    apiCall(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(agentData),
    }),

  // Alias for updateAgent
  updateAgent: (id, agentData) =>
    apiCall(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(agentData),
    }),

  delete: (id) =>
    apiCall(`/agents/${id}`, {
      method: 'DELETE',
    }),

  // Alias for deleteAgent
  deleteAgent: (id) =>
    apiCall(`/agents/${id}`, {
      method: 'DELETE',
    }),

  getStats: (id) =>
    apiCall(`/agents/${id}/stats`, {
      method: 'GET',
    }),
  updateKycStatus: (agentId, flags) =>
    apiCall(`/agents/${agentId}/kyc`, {
      method: 'PUT',
      body: JSON.stringify(flags),
    }),
};

/**
 * Utility function to check API health
 */
export const checkHealth = () =>
  fetch(`${API_BASE_URL}/health`)
    .then((res) => res.json())
    .catch(() => ({ status: 'offline' }));

/**
 * KYC API calls (admin)
 */
export const kycAPI = {
  sendAadhaarOtp: (aadhaarId) =>
    apiCall('/kyc/aadhaar/send-otp', {
      method: 'POST',
      body: JSON.stringify({ aadhaarId }),
    }),

  verifyAadhaarOtp: (aadhaarId, otp, refId) =>
    apiCall('/kyc/aadhaar/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ aadhaarId, otp, refId }),
    }),

  verifyPan: (panId) =>
    apiCall('/kyc/pan/verify', {
      method: 'POST',
      body: JSON.stringify({ panId }),
    }),
};

const api = {
  authAPI,
  loanTypesAPI,
  retailersAPI,
  loansAPI,
  collectionsAPI,
  investmentsAPI,
  reportsAPI,
  agentsAPI,
  checkHealth,
  kycAPI,
};

export default api;
